import { useState } from 'react';
import { AsyncBoundary, Notice } from '@kollez/design-system';
import {
  useAffiliations,
  type AffiliationRow, type AffiliationStatus, type AffiliationType,
} from '@/lib/queries';

/**
 * /group/affiliations (Page 72/N) — Axis-4 college↔university edges.
 *
 * Affiliation records the time-variant link between an autonomous /
 * constituent / affiliated / deemed college and its parent university.
 * Groups need this for NIRF/NAAC (they cite the affiliation date) and
 * for MoU scoping. Server API filters + summarises; no page consumed it.
 */

const TYPE_LABEL: Record<AffiliationType, string> = {
  Affiliated: 'Affiliated',
  Constituent: 'Constituent',
  Autonomous: 'Autonomous',
  Deemed: 'Deemed',
};

const STATUS_LABEL: Record<AffiliationStatus, string> = {
  active: 'Active',
  expired: 'Expired',
  terminated: 'Terminated',
  superseded: 'Superseded',
};

function statusTone(s: AffiliationStatus): string {
  if (s === 'active') return 'badge-success';
  if (s === 'expired') return 'badge-warning';
  if (s === 'terminated') return 'badge-danger';
  return '';
}

function fmt(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function GroupAffiliationsPage() {
  const [type, setType] = useState<AffiliationType | ''>('');
  const [status, setStatus] = useState<AffiliationStatus | ''>('active');
  const [q, setQ] = useState('');
  const [asOf, setAsOf] = useState('');

  const list = useAffiliations({ type, status, q, asOf, sortBy: 'startAt', sortDir: 'desc', limit: 100 });
  const rows = list.data?.rows ?? [];
  const summary = list.data?.summary;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Institutions · Axis-4</p>
          <h1 className="h-page">Affiliations</h1>
          <p className="subhead">
            Every college↔university tie, its type, its window, and whether it's active
            right now. Cited by NIRF/NAAC; scopes what a partner MoU can cover.
          </p>
        </div>
      </div>

      {summary && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <div className="card kpi"><span className="kpi-label">Active</span>
            <span className="kpi-value">{summary.active}</span></div>
          <div className="card kpi"><span className="kpi-label">Expired</span>
            <span className="kpi-value">{summary.expired}</span></div>
          <div className="card kpi"><span className="kpi-label">Terminated</span>
            <span className="kpi-value">{summary.terminated}</span></div>
          <div className="card kpi"><span className="kpi-label">Superseded</span>
            <span className="kpi-value">{summary.superseded}</span></div>
        </div>
      )}

      <div className="card">
        <div className="filterbar">
          <input className="input" placeholder="Search college or university" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="select" value={type} onChange={(e) => setType(e.target.value as AffiliationType | '')}>
            <option value="">Any type</option>
            {(Object.entries(TYPE_LABEL) as Array<[AffiliationType, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as AffiliationStatus | '')}>
            <option value="">Any status</option>
            {(Object.entries(STATUS_LABEL) as Array<[AffiliationStatus, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <input className="input" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} title="Active as of" />
          <span className="fb-count">{list.data?.meta?.total ?? rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No affiliations in scope"
          emptyHint="Add a college and record its university tie to start populating this."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>College</th>
                  <th>University</th>
                  <th>Type</th>
                  <th>Window</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: AffiliationRow) => (
                  <tr key={r._id}>
                    <td>
                      <div className="cell-title">
                        {typeof r.college === 'object' ? r.college.name : '—'}
                      </div>
                      {typeof r.college === 'object' && r.college.aisheCode && (
                        <div className="cell-sub">{r.college.aisheCode}</div>
                      )}
                    </td>
                    <td>
                      <div className="cell-title">
                        {typeof r.university === 'object' ? r.university.name : '—'}
                      </div>
                      {typeof r.university === 'object' && r.university.aisheCode && (
                        <div className="cell-sub">{r.university.aisheCode}</div>
                      )}
                    </td>
                    <td className="cell-sub">{TYPE_LABEL[r.type]}</td>
                    <td className="cell-sub">
                      {fmt(r.startAt)} → {r.endAt ? fmt(r.endAt) : 'open'}
                    </td>
                    <td><span className={`badge ${statusTone(r.status)}`}>{STATUS_LABEL[r.status]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Notice tone="info">
        <b>Axis-4 rule.</b> Affiliation type comes from the regulator (UGC/AICTE). Kollez
        records the edge but never invents its type — always cite the source approval
        letter in the note field when adding a new row.
      </Notice>
    </div>
  );
}
